import React, { useState, useRef, useEffect } from 'react';
import { CanvasWidget, Position, ToolType, WidgetGroup } from './types';
import GlassWidget from './GlassWidget';
import MinimalWidget from './MinimalWidget';
import { Lock, CornerRightDown } from 'lucide-react';
import WidgetDebug from './WidgetDebug';

interface WidgetProps {
  widget: CanvasWidget;
  onUpdate: (widget: CanvasWidget) => void;
  onSelect?: (widgetId: string, isMultiSelect: boolean) => void;
  onHover?: (isHovering: boolean) => void;
  onGrab?: (widgetId: string) => void;
  onDuplicateWidget?: (widgetId: string) => void;
  onMaximizeWidget?: (widgetId: string) => void;
  onLockWidget?: (widgetId: string, isLocked: boolean) => void;
  onManageGroup?: () => void;
  tool: ToolType;
  zoom: number;
  isSelected?: boolean;
  widgetGroup?: WidgetGroup;
  minWidth?: number;  // Add configurable minimum width
  minHeight?: number; // Add configurable minimum height
  allowSmallerThanContent?: boolean; // Add option to allow smaller than content
}

const Widget: React.FC<WidgetProps> = ({ 
  widget, 
  onUpdate, 
  onSelect,
  onHover,
  onGrab,
  onDuplicateWidget,
  onMaximizeWidget,
  onLockWidget,
  onManageGroup,
  tool, 
  zoom,
  isSelected,
  widgetGroup,
  minWidth = 100,  // Default minimum width
  minHeight = 50,  // Default minimum height
  allowSmallerThanContent = true // Default to allowing smaller than content
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState<{x: number, y: number, width: number, height: number}>({
    x: 0, y: 0, width: 0, height: 0
  });
  const widgetRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // Calculate minimum size based on content
  const [contentSize, setContentSize] = useState<{width: number, height: number}>({
    width: widget.size.width,
    height: widget.size.height
  });
  
  // Ref for the content container to measure its size
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Function to update widget size based on content
    const updateWidgetSize = () => {
      if (contentRef.current) {
        // Get the content's size including padding
        const contentWidth = contentRef.current.scrollWidth;
        const contentHeight = contentRef.current.scrollHeight;
        
        // No need for extra padding since we don't have a header anymore
        const requiredHeight = contentHeight;
        const requiredWidth = contentWidth;
        
        // Only update if we don't allow smaller than content
        if (!allowSmallerThanContent && (requiredHeight > widget.size.height || requiredWidth > widget.size.width)) {
          setContentSize({
            width: Math.max(widget.size.width, requiredWidth),
            height: Math.max(widget.size.height, requiredHeight)
          });
        }
      }
    };
    
    // Update after component mounts
    updateWidgetSize();
    
    // Setup resize observer to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidgetSize();
    });
    
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [widget.content, widget.size.height, widget.size.width]);
  
  // Update widget size if content size changed
  useEffect(() => {
    // Only update if the content size is different from the widget size
    // AND we're not currently resizing manually
    // AND we don't allow smaller than content
    if (!isResizing && !allowSmallerThanContent && (contentSize.width > widget.size.width || contentSize.height > widget.size.height)) {
      onUpdate({
        ...widget,
        size: {
          width: contentSize.width,
          height: contentSize.height
        }
      });
    }
  }, [contentSize, widget, onUpdate, isResizing, allowSmallerThanContent]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (tool !== 'select') return;
    
    // Only prevent dragging when clicking buttons, but allow dragging from anywhere else
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('.resize-handle')) {
      return;
    }
    
    e.stopPropagation();
    
    // Bring widget to front when grabbed
    if (onGrab) {
      onGrab(widget.id);
    }
    
    setIsDragging(true);
    setDragStart({
      x: (e.clientX / zoom) - widget.position.x,
      y: (e.clientY / zoom) - widget.position.y
    });
  };
  
  const handleResizeStart = (e: React.PointerEvent) => {
    if (tool !== 'select') return;
    
    e.stopPropagation();
    e.preventDefault();
    
    // Capture the pointer to ensure we continue receiving events even if cursor moves outside
    if (resizeHandleRef.current && 'setPointerCapture' in resizeHandleRef.current) {
      try {
        (resizeHandleRef.current as any).setPointerCapture(e.pointerId);
      } catch (err) {
        // Fallback for browsers that don't support pointer capture
        console.warn('Pointer capture not supported, using fallback');
      }
    }
    
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: widget.size.width,
      height: widget.size.height
    });
    
    // Prevent text selection and other interactions during resize
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';
    document.body.style.cursor = 'nwse-resize';
    
    // Re-enable pointer events on the resize handle
    if (resizeHandleRef.current) {
      resizeHandleRef.current.style.pointerEvents = 'auto';
    }
  };
  
  const handleSelectWidget = () => {
    if (onSelect) {
      // Pass true for multiSelect if Ctrl/Cmd is pressed during click
      const isMultiSelect = window.event && (window.event as KeyboardEvent).ctrlKey;
      onSelect(widget.id, isMultiSelect || false);
    }
  };

  // Handle duplicate, maximize, lock, and group management
  const handleDuplicate = () => {
    onDuplicateWidget && onDuplicateWidget(widget.id);
  };

  const handleMaximize = () => {
    onMaximizeWidget && onMaximizeWidget(widget.id);
  };

  const handleLockPosition = () => {
    onLockWidget && onLockWidget(widget.id, true);
  };

  const handleManageGroup = () => {
    onManageGroup && onManageGroup();
  };
  
  // Handle mouse enter/leave to control canvas zooming
  const handleMouseEnter = () => {
    console.log(`🎯 Widget hover START: ${widget.type} (${widget.id.slice(0, 8)})`);
    if (onHover) {
      onHover(true);
    }
  };
  
  const handleMouseLeave = () => {
    console.log(`🎯 Widget hover END: ${widget.type} (${widget.id.slice(0, 8)})`);
    if (onHover) {
      onHover(false);
    }
  };

  // Enhanced event handlers for widget content to prevent canvas interactions
  const handleContentMouseEnter = (e: React.MouseEvent) => {
    // Ensure hover state is set when entering widget content
    if (onHover) {
      onHover(true);
    }
  };

  const handleContentMouseLeave = (e: React.MouseEvent) => {
    // Only clear hover if we're actually leaving the widget entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !widgetRef.current?.contains(relatedTarget)) {
      if (onHover) {
        onHover(false);
      }
    }
  };

  // Prevent canvas wheel events when hovering over widget content
  const handleContentWheel = (e: React.WheelEvent) => {
    // Always prevent wheel events from propagating to canvas
    e.stopPropagation();
    
    // Allow the widget content to handle its own scrolling
    const target = e.target as HTMLElement;
    const scrollableParent = target.closest('.overflow-auto, .overflow-y-auto, .overflow-x-auto');
    
    if (scrollableParent) {
      // Let scrollable content handle the wheel event naturally
      return;
    }
    
    // For non-scrollable content, prevent the event entirely
    e.preventDefault();
  };

  // Prevent canvas interactions for mouse events on widget content
  const handleContentMouseDown = (e: React.MouseEvent) => {
    // Check if we're clicking on an interactive element
    const target = e.target as HTMLElement;
    const isInteractiveElement = target.matches('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') ||
                                target.closest('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider');
    
    if (isInteractiveElement) {
      // Stop propagation to prevent widget dragging when interacting with form elements
      e.stopPropagation();
      return;
    }
    
    // For non-interactive elements, allow the widget mouseDown handler to process normally
    // but ensure hover state is maintained
    if (onHover) {
      onHover(true);
    }
  };

  // Comprehensive event handler for interactive elements
  const handleInteractiveElementEvent = (e: React.MouseEvent | React.PointerEvent | React.TouchEvent) => {
    // Always stop propagation for interactive elements to prevent canvas interactions
    e.stopPropagation();
    
    // Ensure hover state is active
    if (onHover) {
      onHover(true);
    }
  };

  // Use effect for global mouse event handling when dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const newX = (e.clientX / zoom) - dragStart.x;
      const newY = (e.clientY / zoom) - dragStart.y;
      
      onUpdate({
        ...widget,
        position: { x: newX, y: newY }
      });
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    // Add global event listeners
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    // Clean up
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, zoom, widget, onUpdate]);
  
  // Use effect for global mouse event handling when resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (!isResizing) return;
      
      e.preventDefault();
      
      // Calculate delta movement
      const deltaX = (e.clientX - resizeStart.x) / zoom;
      const deltaY = (e.clientY - resizeStart.y) / zoom;
      
      // Calculate new size with configurable minimums
      const newWidth = Math.max(minWidth, resizeStart.width + deltaX);
      const newHeight = Math.max(minHeight, resizeStart.height + deltaY);
      
      // Update widget size without triggering content size update
      onUpdate({
        ...widget,
        size: { 
          width: newWidth, 
          height: newHeight 
        }
      });
    };

    const handleGlobalPointerUp = (e: PointerEvent) => {
      // Release pointer capture
      if (resizeHandleRef.current && 'releasePointerCapture' in resizeHandleRef.current) {
        try {
          (resizeHandleRef.current as any).releasePointerCapture(e.pointerId);
        } catch (err) {
          // Ignore errors if pointer capture wasn't set
        }
      }
      
      // Restore document styles
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.body.style.cursor = '';
      
      setIsResizing(false);
    };

    // Add global event listeners - use pointer events for better capture
    window.addEventListener('pointermove', handleGlobalPointerMove);
    window.addEventListener('pointerup', handleGlobalPointerUp);
    
    // Fallback to mouse events for older browsers
    window.addEventListener('mousemove', handleGlobalPointerMove as any);
    window.addEventListener('mouseup', handleGlobalPointerUp as any);

    // Clean up
    return () => {
      window.removeEventListener('pointermove', handleGlobalPointerMove);
      window.removeEventListener('pointerup', handleGlobalPointerUp);
      window.removeEventListener('mousemove', handleGlobalPointerMove as any);
      window.removeEventListener('mouseup', handleGlobalPointerUp as any);
      
      // Ensure styles are restored even if cleanup happens unexpectedly
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, resizeStart, zoom, widget, onUpdate, minWidth, minHeight]);

  // Determine the border color based on group and selection state
  const getBorderClass = () => {
    if (isSelected) {
      return 'border-[#47be8f]';
    } else if (widget.groupId && widgetGroup) {
      // Convert hex color to a tailwind-like class
      if (widgetGroup.color.startsWith('#')) {
        return `border-[${widgetGroup.color}]`;
      }
      return `border-${widgetGroup.color}`; 
    }
    return 'border-gray-700/20';
  };
  
  // Determine widget header styling based on whether it's a parent widget in a group
  const getHeaderClass = () => {
    if (widget.isParentWidget && widgetGroup) {
      if (widgetGroup.color.startsWith('#')) {
        return `bg-[${widgetGroup.color}]/20`;
      }
      return `bg-${widgetGroup.color}/20`;
    }
    return 'bg-[#182828]';
  };

  return (
    <div
      ref={widgetRef}
      className={`
        absolute pointer-events-auto widget 
        ${isDragging ? 'dragging' : ''} 
        ${isResizing ? 'resizing' : ''} 
        ${isSelected ? 'selected' : ''}
      `}
      data-widget-type={widget.type}
      style={{
        transform: `translate(${widget.position.x}px, ${widget.position.y}px)`,
        width: widget.size.width,
        height: widget.size.height,
        zIndex: isDragging || isResizing ? 9999 : (widget.zIndex || 1),
        transition: isDragging || isResizing ? 'none' : 'transform 0.2s ease-out',
        willChange: 'transform'
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Use MinimalWidget instead of GlassWidget for a cleaner look */}
      <MinimalWidget
        onClose={() => onUpdate({ ...widget, isDeleted: true })}
        className="h-full"
      >
        {/* Resize overlay - shows during resize to provide visual feedback */}
        {isResizing && (
          <div 
            className="absolute inset-0 z-40 bg-[#47be8f]/5 border-2 border-[#47be8f]/30 rounded-lg pointer-events-none"
            style={{
              cursor: 'nwse-resize'
            }}
          >
            <div className="absolute bottom-2 right-2 text-xs text-[#47be8f] font-mono bg-black/50 px-2 py-1 rounded">
              {Math.round(widget.size.width)} × {Math.round(widget.size.height)}
            </div>
          </div>
        )}
        
        <div 
          ref={contentRef} 
          className={`h-full w-full ${tool === 'select' ? 'cursor-move' : ''} ${!widget.content ? 'flex items-center justify-center' : ''} ${widget.size.width < contentSize.width || widget.size.height < contentSize.height ? 'overflow-auto custom-scrollbar' : ''}`}
          style={{
            pointerEvents: isResizing ? 'none' : 'auto'
          }}
          onMouseEnter={handleContentMouseEnter}
          onMouseLeave={handleContentMouseLeave}
          onWheel={handleContentWheel}
          onMouseDown={handleContentMouseDown}
          onPointerDown={handleInteractiveElementEvent}
          onTouchStart={handleInteractiveElementEvent}
        >
          {widget.content || <Lock size={24} className="text-gray-400" />}
        </div>
        
        {/* Add debugging info */}
        {process.env.NODE_ENV !== 'production' && (
          <WidgetDebug widgetId={widget.id} widgetType={widget.type} />
        )}
      </MinimalWidget>
      
      {/* Resize handle - keep this to allow resizing */}
      {tool === 'select' && (
        <div 
          ref={resizeHandleRef}
          className={`
            resize-handle absolute bottom-0 right-0 w-6 h-6 
            cursor-nwse-resize z-50 flex items-center justify-center
            transition-colors duration-150
            ${isResizing ? 'bg-[#47be8f]/20' : 'bg-transparent hover:bg-[#47be8f]/10'}
            ${isSelected ? 'border-t border-l border-[#47be8f]/30' : 'border-t border-l border-white/10 hover:border-[#47be8f]/20'}
            rounded-tl-md
          `}
          onPointerDown={handleResizeStart}
          style={{
            pointerEvents: 'auto',
            touchAction: 'none'
          }}
        >
          <div className={`
            w-4 h-4 flex items-center justify-center 
            transition-opacity 
            ${isSelected ? 'opacity-100' : 'opacity-50 hover:opacity-100'}
          `}>
            <CornerRightDown 
              size={14} 
              className={`
                text-[#47be8f]
                ${isResizing ? 'opacity-100' : isSelected ? 'opacity-90' : 'opacity-70'} 
                transition-opacity
              `} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Widget;